import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/client';

export interface NotificationItem {
  id: string;
  recipientId: string;
  title: string;
  message: string;
  type: 'message' | 'system' | 'alert' | 'action' | string;
  actionUrl?: string;
  read: boolean;
  created_at: string;
}

export interface NotificationPreference {
  id: string;
  userId: string;
  category: string;
  channel: 'IN_APP' | 'PUSH' | 'EMAIL' | 'SMS';
  enabled: boolean;
}

export interface NotificationsState {
  items: NotificationItem[];
  preferences: NotificationPreference[];
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  items: [],
  preferences: [],
  loading: false,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.notifications.list();
      if (!res.success) {
        return rejectWithValue(res.message || 'Failed to fetch notifications');
      }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markNotificationRead',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.notifications.markAsRead(id);
      if (!res.success) {
        return rejectWithValue(res.message || 'Failed to mark notification as read');
      }
      return id;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllNotificationsRead',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.notifications.markAllAsRead();
      if (!res.success) {
        return rejectWithValue(res.message || 'Failed to mark all as read');
      }
      return true;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const fetchPreferences = createAsyncThunk(
  'notifications/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.notifications.getPreferences();
      if (!res.success) {
        return rejectWithValue(res.message || 'Failed to fetch preferences');
      }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const updatePreference = createAsyncThunk(
  'notifications/updatePreference',
  async (payload: { category: string; channel: string; enabled: boolean }, { rejectWithValue }) => {
    try {
      const res = await api.notifications.updatePreference(payload);
      if (!res.success) {
        return rejectWithValue(res.message || 'Failed to update preference');
      }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const registerDevice = createAsyncThunk(
  'notifications/registerDevice',
  async (payload: { deviceId: string; fcmToken: string; platform: string; appVersion?: string; appInstanceId: string }, { rejectWithValue }) => {
    try {
      const res = await api.notifications.registerDevice(payload);
      if (!res.success) {
        return rejectWithValue(res.message || 'Failed to register device');
      }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addIncomingNotification: (state, action) => {
      if (!state.items.find(item => item.id === action.payload.id)) {
        state.items.unshift(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const item = state.items.find(i => i.id === action.payload);
        if (item) {
          item.read = true;
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items.forEach(item => {
          item.read = true;
        });
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload || [];
      })
      .addCase(updatePreference.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.preferences.findIndex(
          (p: NotificationPreference) => p.category === updated.category && p.channel === updated.channel
        );
        if (idx !== -1) {
          state.preferences[idx].enabled = updated.enabled;
        } else {
          state.preferences.push(updated);
        }
      });
  },
});

export const { addIncomingNotification } = notificationsSlice.actions;
export default notificationsSlice.reducer;
